import { Box, TextField } from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import {
  ISSUE_DETAIL_CHAR_CAP,
  ISSUE_DETAIL_MIN_CHARS,
  isIssueDetailSufficient,
} from '../../redux/features/issueReport/issueReport.slice';
import {
  IssueClassifySurface,
  IssueReportRecord,
} from '../../redux/features/issueReport/issueReport.props';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import IssueTypeChips from './IssueTypeChips';

/**
 * ONE sentence for the mandatory detail (HITL-1608), and it is deliberately one.
 *
 * It first shipped as two — a `(required)` placeholder and a separate
 * `Add at least 3 characters…` error — which said the same thing twice in two
 * different voices and left the agent reading both to work out which applied. The
 * rule is a single fact, so it gets a single wording, and the field moves it between
 * two slots rather than duplicating it: the PLACEHOLDER while the field is empty
 * (and the placeholder is what the agent can actually see), the HELPER line once
 * there is text covering the placeholder. It is never rendered in both at once.
 *
 * Stating the minimum up front is the point of clubbing them. The old placeholder
 * said only "(required)", so the count was a surprise delivered after a click was
 * refused; now the agent knows the whole rule before they reach for a chip.
 */
export const ISSUE_DETAIL_PROMPT = `Add a few more details (required, min ${ISSUE_DETAIL_MIN_CHARS} characters)`;

/**
 * The one state that needs different words. The field is visibly full, so
 * "min 3 characters" would read as nonsense — what the agent has to be told is that
 * the half-sentence is the MACHINE's and finishing it is the job.
 */
export const ISSUE_DETAIL_AUTOFILL_PROMPT =
  'Finish the sentence — say what the customer actually said.';

export interface IssueClassifyHandle {
  /**
   * Drop the pending detail so the unmount commit does not re-send it.
   *
   * `Discard` retracts the whole report (`voidIssue`), and the host owns that
   * dispatch — but the "has this already been committed?" latch lives in here with
   * the text it guards, so the host has to say so imperatively.
   */
  discard(): void;
}

interface IssueClassifyBodyProps {
  /**
   * The report being classified. Everything the body needs is read from the RECORD,
   * never from props: the report is already filed by the time any host mounts this,
   * so the record is the one source for what it carries.
   */
  record: IssueReportRecord;
  /**
   * Which surface this is mounted in, emitted as `classified_from` when a chip is
   * clicked. REQUIRED, deliberately: it is the only way the reversal's per-surface
   * conversion is measurable, and a host that forgets it would silently emit nulls
   * that read as "nobody classified this".
   */
  surface: IssueClassifySurface;
  /**
   * A classification gesture finished — the host should close itself.
   *
   * Fires on a chip click and on Enter. Note that Enter fires it even when nothing
   * was sent (an untouched seed): Enter's contract is "always closes, only sends when
   * there is something new", so this means "done with me", not "a write happened".
   */
  onCommitted?: () => void;
  /**
   * The FIRST keystroke of a typing episode, re-armed on blur.
   *
   * Deliberately not `onFocus`. The field is focused automatically when a host opens,
   * so pausing on focus meant the countdown never ran and every panel lived to the
   * 60s hard cap. Per-keystroke would be a second clock racing the first, and the dial
   * would stutter as the agent thinks.
   */
  onDetailFirstInput?: () => void;
  /** Every blur. The flag panel resumes its clock from the banked remainder. */
  onDetailBlur?: () => void;
  /** Take the caret on mount, with it placed after the seed. */
  autoFocusDetail?: boolean;
  /**
   * Whether Enter in the detail field commits. The flag panel: true. Backlog
   * surfaces: false — there is no 10s window there to race, so the only reason to
   * bind a sending key is absent.
   */
  enterSubmits?: boolean;
}

/**
 * HITL-1572: the classify surface — a detail field and the whole taxonomy — with the
 * record-shaped logic that three different hosts would otherwise each reimplement.
 *
 * Split from `IssueFlagPanel` along one line: *is this about the record, or about the
 * 10-second window?* Everything here is about the record, so it holds for a backlog
 * row with no window at all. The window — the dial, pause/resume, the window-level
 * Escape, the caret handback, `Discard` — stays with the panel.
 *
 * ## The four things a host must not have to re-derive
 *
 * 1. **Seed handling.** A word click files a half-written sentence; the field opens
 *    with it, and an UNCHANGED seed must never go out as a second revision.
 * 2. **Type preservation.** A detail-only commit must not erase a type the filing
 *    gesture already chose.
 * 3. **Commit on unmount.** The surface can vanish from under the agent — the panel's
 *    clock runs out, a task auto-assigns off the lobby — and the typed detail is the
 *    only signal saying what the taxonomy is missing.
 * 4. **What Enter does, and what it attributes.** See `handleDetailKeyDown`.
 *
 * Getting any of them subtly wrong is silent: a report goes out with a sentence the
 * agent never typed, or loses a classification, or drops the text entirely.
 *
 * HITL-1576 added `surface`, so an amend says WHERE the agent classified from. Only a
 * chip click carries it — a detail is not a triage decision.
 *
 * ## HITL-1608: the detail is MANDATORY, and this is the only place that enforces it
 *
 * A type commits only when the field holds agent-written text — see
 * `isIssueDetailSufficient`, which owns the rule. Enforced here rather than in the
 * slice on purpose: `commitIssueType` is also how a detail-only amend and a retraction
 * travel, and a reducer that refused an insufficient detail would have to distinguish
 * those, while every surface that could bypass the rule already mounts this component.
 *
 * The reason is what agents were actually filing: a chip click with an empty field
 * costs one keystroke and says nothing but its category, so most reports arrived with
 * no way to tell what happened. The cost of the fix is real and accepted — an agent who
 * will not type now classifies NOTHING, and the report lands in the outstanding backlog
 * instead of arriving mislabelled-but-counted. `Later`, `Discard`, Escape and the
 * countdown are deliberately untouched, so the rule can always be walked away from.
 */
const IssueClassifyBody = React.forwardRef<
  IssueClassifyHandle,
  IssueClassifyBodyProps
>(
  (
    {
      record,
      surface,
      onCommitted,
      onDetailFirstInput,
      onDetailBlur,
      autoFocusDetail = false,
      enterSubmits = false,
    },
    ref
  ) => {
    const { commitIssueType } = useIssueReport();
    const [detail, setDetail] = useState('');
    /**
     * The seed again, as STATE this time (HITL-1608).
     *
     * `seededDetailRef` below cannot be read during render — it is written inside the
     * effect — and the chips' enabled state now depends on the seed on every
     * keystroke. Both are set in the same effect from the same value, so they cannot
     * disagree; the ref serves the unmount cleanup, this serves the render.
     */
    const [seededDetail, setSeededDetail] = useState('');
    /**
     * Whether to SHOW the required-state. Separate from whether the detail is
     * sufficient, because an untouched field is not an error — it is just empty. This
     * latches only once the agent has tried to classify without one, so the panel does
     * not open shouting at someone who has not done anything yet.
     */
    const [showDetailRequired, setShowDetailRequired] = useState(false);
    const detailRef = useRef<HTMLInputElement | null>(null);

    // The detail the agent typed must survive the unmount that closing causes, so the
    // cleanup commit reads a ref rather than state torn down mid-effect.
    const detailValueRef = useRef('');
    detailValueRef.current = detail;
    /**
     * Whether the host has already been told about this typing episode. Re-armed on
     * blur, so a blur-then-type pauses the clock again rather than leaving it running
     * while the agent writes.
     */
    const firstInputSignalledRef = useRef(false);
    // Set once a chip has already committed this record's detail, so the unmount
    // cleanup does not send it a second time at the next revision. Also what
    // `discard()` sets.
    const committedRef = useRef(false);
    /**
     * The detail this surface OPENED with, when the gesture that filed it seeded one
     * (the word-click prefills `"burger" should be `).
     *
     * Held so the unmount commit can tell a seed the agent never finished from text
     * they actually wrote. Without it every timed-out word-click would emit a second
     * revision carrying a sentence the agent did not type.
     */
    const seededDetailRef = useRef('');
    /**
     * The type the record already carries, read by the unmount commit and by Enter.
     *
     * That commit sends a typed detail with the type alongside it, and `reportAmended`
     * assigns the type unconditionally — so passing null there would ERASE a
     * classification the gesture had already made. A word click files
     * `Incorrect Transcription`; without this, finishing the sentence wiped it.
     *
     * Captured when the record OPENS, not during render: a host can null its active
     * report as it closes, so the last render before unmount may have no record to
     * read and a render-time assignment would put back the null this exists to
     * prevent. A chip click cannot be missed either — it sets `committedRef` and the
     * cleanup returns before reaching this.
     */
    const activeTypeRef = useRef<string | null>(null);
    /**
     * Whether the text this surface opened with was the MACHINE's (see
     * `IssueReportRecord.detail_autofilled`). A ref, not the prop read directly,
     * because the commit callbacks must stay referentially stable — they feed a
     * memoized 24-chip grid — and because, like `activeTypeRef`, it has to survive a
     * host nulling its record as it closes.
     */
    const detailAutofilledRef = useRef(false);

    /**
     * Does what is currently typed satisfy the mandatory-detail rule? Reads the LIVE
     * refs, so it can be called from the stable callbacks without putting `detail` in
     * their dependency lists and re-rendering every chip on every keystroke.
     */
    const hasSufficientDetail = useCallback(
      () =>
        isIssueDetailSufficient(detailValueRef.current, {
          seededDetail: seededDetailRef.current,
          isAutofilled: detailAutofilledRef.current,
        }),
      []
    );

    /**
     * A classification gesture arrived with nothing to attach it to: show the
     * required-state and put the caret where the agent has to act.
     *
     * Also banks the countdown, the same way the first keystroke does. The agent has
     * just declared they intend to classify this report, so spending their remaining
     * seconds watching them read the error would be the one outcome the window exists
     * to prevent. Guarded by the same latch as a keystroke, so it pauses once per
     * typing episode and a later blur still resumes exactly once.
     */
    const rejectForDetail = useCallback(() => {
      setShowDetailRequired(true);
      if (!firstInputSignalledRef.current) {
        firstInputSignalledRef.current = true;
        onDetailFirstInput?.();
      }
      const field = detailRef.current;
      if (!field) return;
      field.focus();
      const end = field.value.length;
      field.setSelectionRange(end, end);
    }, [onDetailFirstInput]);

    useImperativeHandle(
      ref,
      () => ({
        discard: () => {
          committedRef.current = true;
        },
      }),
      []
    );

    /**
     * One record, one detail — and the cleanup is also where a detail the agent typed
     * but never committed gets recorded.
     *
     * Keyed on the record id ALONE. Re-running when the record's own `issue_detail`
     * changes would reset the field out from under the agent on every amend. Reset in
     * the effect rather than relying on unmount, because a host may keep this mounted
     * across records.
     */
    useEffect(() => {
      // A trailing space is added for CONTINUATION: the record stores the sanitized
      // sentence (`"burger" should be`, trimmed for the wire), but the agent is meant
      // to type the next word onto the end of it, and the caret must not land flush
      // against `be`. The unchanged-seed comparison below trims both sides, so this
      // cannot make an untouched seed look edited.
      const stored = record.issue_detail ?? '';
      const seed = stored ? `${stored} ` : '';
      seededDetailRef.current = seed;
      setSeededDetail(seed);
      activeTypeRef.current = record.issue_type ?? null;
      detailAutofilledRef.current = record.detail_autofilled;
      setDetail(seed);
      committedRef.current = false;
      firstInputSignalledRef.current = false;
      // Per record: a required-state raised on the last report must not greet the
      // agent on the next one.
      setShowDetailRequired(false);

      // Focused explicitly rather than via the `autoFocus` prop, and on a timeout: the
      // field has to exist and the surrounding DOM settle before it can take the caret.
      // The timeout is also what puts this AFTER the click handback that opened it.
      const focusTimer = autoFocusDetail
        ? setTimeout(() => {
            const field = detailRef.current;
            if (!field) return;
            field.focus();
            // Caret AFTER the seed, so the agent completes the sentence rather than
            // landing in front of it or having to press End first. An unseeded surface
            // puts it at 0, which is the same thing.
            const end = field.value.length;
            field.setSelectionRange(end, end);
          }, 0)
        : undefined;

      return () => {
        if (focusTimer) clearTimeout(focusTimer);
        const typed = detailValueRef.current.trim();
        if (committedRef.current) return;
        // The same rule the chips are gated on, for the same reason plus one: an
        // unchanged seed is not agent input (compared trimmed, because the seed ends
        // in a space and `sanitizeIssueDetail` trims it away anyway), and a one- or
        // two-character remnant is not a detail worth spending a revision on now that
        // three is the floor everywhere else.
        if (
          !isIssueDetailSufficient(typed, {
            seededDetail: seededDetailRef.current,
            // Both from refs, for the reason spelled out on `activeTypeRef`: this
            // runs during teardown, and the `record` this effect closed over is a
            // render old.
            isAutofilled: detailAutofilledRef.current,
          })
        ) {
          return;
        }
        commitIssueType({
          issue_id: record.issue_id,
          // Preserved, not nulled — see activeTypeRef.
          issue_type: activeTypeRef.current,
          issue_detail: typed,
          // The surface is going away without the agent choosing a type, so nothing
          // was classified here. Attributing this would count an abandoned
          // classification as a completed one.
          classified_from: null,
        });
      };
      // commitIssueType is stable across renders (useCallback in useIssueReport).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record.issue_id]);

    /**
     * Every chip behaves identically — and since HITL-1608 that includes the one
     * blocking branch: no chip commits without a detail.
     *
     * The branch is on the DETAIL, never on the type. `Not listed` still behaves
     * exactly like the other 23 (it used to be the candidate for a confirm step, which
     * bought nothing when re-classifying is just another revision), and the rule
     * deliberately does not vary by category — one rule the agent learns once beats
     * twenty-four they have to remember.
     *
     * A blocked click emits NOTHING, not even a detail-only revision. It is not a
     * classification and not an amend; it is the agent being told to finish.
     */
    const handleSelect = useCallback(
      (issueType: string) => {
        if (!hasSufficientDetail()) {
          rejectForDetail();
          return;
        }
        committedRef.current = true;
        commitIssueType({
          issue_id: record.issue_id,
          issue_type: issueType,
          issue_detail: detailValueRef.current.trim() || null,
          // The ONE place a surface is attributed: a chip click is the agent making a
          // triage decision, which is the thing worth counting per surface.
          classified_from: surface,
        });
        // The thunk deliberately does not close anything — it is also used by the
        // timeout path, where the window has already gone. Closing is the host's job,
        // and a chip click must close IMMEDIATELY rather than wait out a countdown.
        onCommitted?.();
      },
      [
        commitIssueType,
        hasSufficientDetail,
        onCommitted,
        record.issue_id,
        rejectForDetail,
        surface,
      ]
    );

    const handleDetailChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!firstInputSignalledRef.current) {
          firstInputSignalledRef.current = true;
          onDetailFirstInput?.();
        }
        const next = e.target.value;
        setDetail(next);
        // Cleared the moment the rule is met, not on the first keystroke: the message
        // says what is still missing, so it has to stay up while it still is. It never
        // re-raises itself here — only another blocked gesture does that.
        if (
          showDetailRequired &&
          isIssueDetailSufficient(next, {
            seededDetail: seededDetailRef.current,
            isAutofilled: detailAutofilledRef.current,
          })
        ) {
          setShowDetailRequired(false);
        }
      },
      [onDetailFirstInput, showDetailRequired]
    );

    const handleDetailBlur = useCallback(() => {
      firstInputSignalledRef.current = false;
      onDetailBlur?.();
    }, [onDetailBlur]);

    /**
     * Enter commits the typed detail and closes. One key, one result, every report.
     *
     * ## It used to be gated on the two ASR types, and HITL-1576 removed that
     *
     * The gate existed because on an action-bar flag no type has been chosen yet, so
     * an Enter pressed out of habit committed `issue_type: null` — and `Unclassified`
     * was TERMINAL. A reflex keystroke permanently destroyed the classification on the
     * most common path, which is about as bad as a single keypress can be.
     *
     * HITL-1571 made a null-typed commit RECOVERABLE: it is exactly what puts a report
     * in the outstanding backlog, where the agent picks it up from the Issue button or
     * the task-router lobby. The mistake the gate prevented no longer exists, so the
     * gate is gone — and with it the surprise of a key that silently did nothing on the
     * most common report while working on the rarest.
     *
     * The ordering was not a convenience: relaxing this before those surfaces existed
     * would have let a reflex keystroke commit a null type with nowhere to resurface.
     *
     * ## Kept exactly as it was
     *
     * The untouched-seed check. Revision 0 already carries a word click's seeded
     * sentence, so Enter on an unfinished prefill closes without emitting a revision
     * identical to the one before it. Enter always CLOSES; it only SENDS when there is
     * something new. And a mid-composition Enter still commits the IME candidate rather
     * than the report.
     *
     * `activeTypeRef`, not a re-derivation: a detail-only commit must carry whatever
     * type the record already has, or `reportAmended` would assign null over it.
     *
     * ## With a chip selected, Enter IS pressing that chip
     *
     * So it attributes the surface, and the report leaves the backlog confirmed. This
     * first shipped attributing nothing on the grounds that "Enter sends a detail, not
     * a classification" — true only when no type is selected. On a word click the chip
     * is pre-selected from the machine's guess, and QA found the consequence: the agent
     * typed a detail, pressed Enter, the panel sent the type, and the report was STILL
     * listed as needing classification. Enter closes the panel and sends the type; a
     * gesture that does both cannot also mean "I have not decided".
     *
     * With NO type selected it still attributes nothing, which on the panel means an
     * action-bar flag: the typed sentence is all there is, and counting it as a triage
     * decision would inflate exactly the number `classified_from` exists to measure. A
     * chip click commits and closes immediately, so a type can only be active here
     * because the report arrived pre-typed from a transcript gesture.
     *
     * The untouched-seed case is deliberately NOT confirmation. A word click seeds an
     * unfinished sentence, so Enter on a prefill nobody has touched is as likely to be
     * a reflex to get back to the omnibar as it is a decision — and the ambiguous
     * gesture takes the recoverable outcome: the panel closes and the report stays in
     * the backlog.
     *
     * ## HITL-1608: Enter STILL ALWAYS CLOSES, and is not blocked like a chip
     *
     * The mandatory-detail rule tightened what Enter SENDS — `hasSufficientDetail()`
     * is now the same gate the chips use, so a two-character remnant no longer goes
     * out — but it did not change what Enter does to the panel. A blocked chip click
     * holds the surface open and explains itself; a blocked Enter would not be read
     * that way. This panel sits inches from the omnibar, it takes the caret on open,
     * and Enter is the key an agent hits by reflex to get back to their order
     * mid-drive-thru. Making it refuse to close would turn the rule into something
     * that traps a busy agent, and the one thing this change must not do is stand
     * between them and the car at the board.
     *
     * So the chips teach the rule and Enter stays an exit. Nothing is lost by
     * closing: the report is already filed, and an unclassified one is in the
     * outstanding backlog for the rest of the shift.
     */
    const handleDetailKeyDown = useCallback(
      // Typed against the TextField's ROOT, which MUI renders as a div.
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!enterSubmits) return;
        if (e.key !== 'Enter') return;
        // Mid-composition Enter commits the IME candidate, not the report.
        if (
          (e.nativeEvent as unknown as { isComposing?: boolean }).isComposing
        ) {
          return;
        }
        e.preventDefault();

        const typed = detailValueRef.current.trim();
        if (hasSufficientDetail()) {
          // Preserved, not re-derived — see activeTypeRef.
          const issueType = activeTypeRef.current;
          committedRef.current = true;
          commitIssueType({
            issue_id: record.issue_id,
            issue_type: issueType,
            issue_detail: typed,
            // Submitting WITH a type selected is accepting that type — see above.
            // With none, this is a detail and nothing more.
            classified_from: issueType ? surface : null,
          });
        }
        onCommitted?.();
      },
      [
        commitIssueType,
        enterSubmits,
        hasSufficientDetail,
        onCommitted,
        record.issue_id,
        surface,
      ]
    );

    /** Which wording applies — see ISSUE_DETAIL_AUTOFILL_PROMPT. */
    const isUnfinishedAutofill =
      record.detail_autofilled &&
      detail.trim().length > 0 &&
      detail.trim() === seededDetail.trim();
    /**
     * The placeholder is only VISIBLE while the field is empty, so that is exactly
     * when it can carry the message — and exactly when the helper line must not
     * repeat it. With text in the field the placeholder is hidden and the helper
     * takes over. One message, one place, never both.
     */
    const isPromptVisibleInPlaceholder = detail.length === 0;

    return (
      <Box data-testid="issue-classify-body">
        {/* ABOVE the chips: with one global escape hatch this is the only thing
            that says WHAT the taxonomy missed, and the flow reads top-to-bottom —
            type the context, then click the type that sends it. */}
        <TextField
          inputRef={detailRef}
          value={detail}
          onChange={handleDetailChange}
          onKeyDown={handleDetailKeyDown}
          // Typing PAUSES the host's countdown; blur resumes from the banked
          // remainder. Not "typing extends": extension has no exit when an agent
          // is pulled into an order mid-sentence.
          onBlur={handleDetailBlur}
          placeholder={ISSUE_DETAIL_PROMPT}
          fullWidth
          size="small"
          required
          error={showDetailRequired}
          // Reserved with a space rather than conditionally rendered — an appearing
          // helper line would shift the whole 24-chip grid down under the agent's
          // cursor at the exact moment they are aiming at it.
          //
          // Empty field: silent, because the placeholder below is already saying it
          // (in red, once a click has been refused). Text in the field: the
          // placeholder is covered, so the message moves here.
          helperText={
            showDetailRequired && !isPromptVisibleInPlaceholder
              ? isUnfinishedAutofill
                ? ISSUE_DETAIL_AUTOFILL_PROMPT
                : ISSUE_DETAIL_PROMPT
              : ' '
          }
          // `as never`: MUI's slot prop types do not admit data-* attributes, the
          // same cast FlagIssueButton makes on its tooltip slots.
          FormHelperTextProps={
            { 'data-testid': 'issue-detail-helper' } as never
          }
          inputProps={{
            maxLength: ISSUE_DETAIL_CHAR_CAP,
            'aria-required': true,
            'aria-invalid': showDetailRequired,
          }}
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: 'rgba(255,255,255,0.08)',
              color: Colors.white,
              fontSize: HITL3_FONT_SIZE,
            },
            '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
            // A clear focused border: this field takes the caret on open, so the
            // agent must be able to see at a glance where their keystrokes are
            // going — the omnibar is inches away.
            '& .Mui-focused fieldset': {
              borderColor: `${Colors.green} !important`,
              borderWidth: '2px !important',
            },
            // The required-state outranks focus, and has to: a refused click leaves
            // the caret IN this field, so the green focus ring would otherwise paint
            // straight over the only signal saying why nothing happened.
            ...(showDetailRequired
              ? {
                  '& fieldset, & .Mui-focused fieldset': {
                    borderColor: `${Colors.bittersweet} !important`,
                    borderWidth: '2px !important',
                  },
                }
              : {}),
            // The placeholder IS the error message while the field is empty, so it
            // has to redden with the border — a grey hint under a red border reads
            // as the field asking for something unrelated to what just failed.
            // `opacity: 1` because MUI dims placeholders by default, which would
            // mute the one line the agent needs to read.
            ...(showDetailRequired
              ? {
                  '& .MuiInputBase-input::placeholder': {
                    color: Colors.bittersweet,
                    opacity: 1,
                  },
                }
              : {}),
            // The helper line is dark-on-dark by default in this panel.
            '& .MuiFormHelperText-root': {
              mx: 0,
              fontSize: '0.68rem',
              color: 'rgba(255,255,255,0.45)',
            },
            '& .MuiFormHelperText-root.Mui-error': {
              color: Colors.bittersweet,
            },
          }}
        />

        {/* No height cap and no scroll: all 24 chips are meant to be readable in one
            glance, and a scrollbar would hide part of the taxonomy behind a gesture —
            which is the exact failure (a value buried below the fold) this exists to
            fix.

            One row per category is part of that: a wrapped row reads as two groups and
            costs the height a scrollbar would have saved. */}
        <Box sx={{ mt: 1.5 }}>
          {/* Deliberately told NOTHING about the detail field: the chips stay at
              full colour whether or not the rule is satisfied, and a click that
              cannot be honoured is answered by the field above rather than by
              fading the taxonomy. See the note on IssueTypeChips. */}
          <IssueTypeChips
            onSelect={handleSelect}
            selectedValue={record.issue_type}
          />
        </Box>
      </Box>
    );
  }
);

IssueClassifyBody.displayName = 'IssueClassifyBody';

export default IssueClassifyBody;
